import { ReactNode, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import classNames from 'classnames';
import EventDTO from '../../models/event';
import { CheckoutSessionDTO } from '../../models/checkout-session';
import money from '../../utils/checkoutMoney';
import { parseFriendlyDate } from '../../utils/dates';
import getCustomTicketLabel from '../../utils/getCustomTicketLabel';

interface OrderSummaryProps {
  event: EventDTO;
  session: CheckoutSessionDTO;
  // En el celular arranca plegado; en la columna de la compu, abierto.
  collapsible?: boolean;
  defaultOpen?: boolean;
  // El cargo por servicio se muestra recién en Pago, como en el checkout de
  // siempre. Antes, el monto es el de las entradas sin el cargo.
  showServiceCharge?: boolean;
  // Promos de cuotas, código de descuento, botón de continuar (compu).
  children?: ReactNode;
}

// "2 entradas + 1 extra", con la palabra del evento (inscripción, mesa): el
// estacionamiento ya no se cuenta como una entrada más.
export const describeCount = (session: CheckoutSessionDTO, event?: EventDTO | null) => {
  const entries = session.items.filter((item) => !item.is_extra);
  const count = entries.reduce((sum, item) => sum + item.quantity, 0);
  const extras = session.items.filter((item) => item.is_extra).reduce((sum, item) => sum + item.quantity, 0);
  const label = event
    ? getCustomTicketLabel(event, { plural: count !== 1, casing: 'lower' })
    : `entrada${count === 1 ? '' : 's'}`;
  return `${count} ${label}${extras > 0 ? ` + ${extras} ${extras === 1 ? 'extra' : 'extras'}` : ''}`;
};

export const describeItems = (session: CheckoutSessionDTO, event?: EventDTO | null) => {
  const names = Array.from(new Set(session.items.filter((item) => !item.is_extra).map((item) => item.name)));
  return `${describeCount(session, event)}${names.length === 1 ? ` · ${names[0]}` : ''}`;
};

// Lo que suman las entradas sin el cargo por servicio, con el descuento.
export const amountBeforeCharge = (session: CheckoutSessionDTO) => session.totals.total - session.totals.service_charge;

/**
 * Resumen de la compra: monto, desglose (entradas, descuento y, en Pago, el
 * cargo por servicio) y lo que se cargue debajo.
 */
const OrderSummary = ({
  event, session, collapsible = true, defaultOpen = false, showServiceCharge = false, children,
}: OrderSummaryProps) => {
  const [open, setOpen] = useState(!collapsible || defaultOpen);
  const { totals } = session;
  const amount = showServiceCharge ? totals.total : amountBeforeCharge(session);
  // Cerrado, el cargo igual queda dicho: en Pago no se esconde.
  const closedLabel = showServiceCharge && totals.service_charge > 0
    ? `Incluye ${money(totals.service_charge)} de cargo por servicio`
    : 'Ver detalle del total';
  const toggleLabel = open ? 'Ocultar detalle' : closedLabel;
  const detail = (
    <div className="flex flex-col gap-2 text-sm">
      {session.items.map((item) => (
        <div key={item.ticket_id} className="flex justify-between gap-3">
          <span>{`${item.quantity} × ${item.name}`}</span>
          <span>{money((item.unit_final_price - item.unit_service_charge) * item.quantity)}</span>
        </div>
      ))}
      {showServiceCharge && totals.service_charge > 0 && (
        <div className="flex justify-between gap-3 text-text-secondary">
          <span>Cargo por servicio</span>
          <span>{money(totals.service_charge)}</span>
        </div>
      )}
      {totals.discount > 0 && (
        <div className="flex justify-between gap-3 text-success-700 dark:text-success-300">
          <span>{session.coupon ? `Descuento (${session.coupon.code})` : 'Descuento'}</span>
          <span>{`− ${money(totals.discount)}`}</span>
        </div>
      )}
      <div className="flex items-center justify-between gap-3 border-t border-border-secondary pt-2">
        <span className="font-bold">{showServiceCharge ? 'Total' : 'Subtotal'}</span>
        <span className="text-2xl font-bold">{money(amount)}</span>
      </div>
    </div>
  );

  if (!collapsible) {
    return (
      <aside className="flex flex-col gap-4 rounded-3xl border border-border-secondary bg-surface-base p-5" aria-label="Resumen de la compra">
        <div className="flex flex-col">
          <span className="font-bold">{event.name}</span>
          {event.date && <span className="text-sm text-text-secondary">{parseFriendlyDate(event.date, true, true)}</span>}
        </div>
        {detail}
        {children}
      </aside>
    );
  }

  return (
    <div className="rounded-3xl bg-surface-secondary">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex min-h-[44px] w-full items-center justify-between gap-3 rounded-3xl p-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
      >
        <span className="flex flex-col">
          <span className="font-bold">{describeItems(session, event)}</span>
          <span className="text-sm text-text-secondary">{toggleLabel}</span>
        </span>
        <span className="flex items-center gap-2 text-xl font-bold">
          {money(amount)}
          <FiChevronDown aria-hidden className={classNames('transition-transform', open && 'rotate-180')} />
        </span>
      </button>
      {open && (
        <div className="flex flex-col gap-3 px-4 pb-4">
          {detail}
          {children}
        </div>
      )}
    </div>
  );
};

export default OrderSummary;
